import React from "react";
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  getMetricsTableMinWidth,
  getMetricsTableMinWidthWithLeadingSticky,
  METRICS_TABLE_MIN_DATA_COL_WIDTH,
  metricsTableBaseSx,
  metricsTableDataBodyCellSx,
  metricsTableDataHeaderCellSx,
  metricsTableStickyDataCellSx,
  metricsTableStickyHeaderLabelCellSx,
  metricsTableStickyLabelRowCellSx,
} from "./metricsTableLayout";
import { MetricsTableColgroup } from "./MetricsTableColgroup";

export type MetricsTableColumn = {
  id: string;
  label: string;
  /** Secondary line under the header label (e.g. stream date). */
  subtitle?: string;
  icon?: React.ReactNode;
};

export type MetricsTableRow = {
  key: string;
  label: string;
  /** One value per column, in column order. */
  values: string[];
};

export type MetricsTableGroup = {
  key: string;
  label?: string;
  rows: MetricsTableRow[];
};

export const EMPTY_CELL = "—";

function MetricsTableColumnHeader({ col }: { col: MetricsTableColumn }) {
  if (!col.subtitle) {
    return (
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{ width: "100%", minWidth: 0 }}
      >
        {col.icon ? (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              lineHeight: 0,
            }}
          >
            {col.icon}
          </Box>
        ) : null}
        <Typography
          variant="body2"
          fontWeight={700}
          title={col.label}
          noWrap
          sx={{ minWidth: 0 }}
        >
          {col.label}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={0.25} sx={{ minWidth: 0, width: "100%" }}>
      <Typography
        variant="body2"
        fontWeight={700}
        title={col.label}
        sx={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          lineHeight: 1.25,
          width: "100%",
          wordBreak: "break-word",
          overflowWrap: "anywhere",
        }}
      >
        {col.label}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap>
        {col.subtitle}
      </Typography>
    </Stack>
  );
}

/**
 * Generic comparison grid: platforms as columns, metrics as rows.
 * First column is sticky so labels stay visible while scrolling horizontally.
 */
export const MetricsComparisonTable: React.FC<{
  columns: MetricsTableColumn[];
  groups: MetricsTableGroup[];
  /** Override minimum width per data column (default 100px). */
  minDataColWidth?: number;
  /**
   * Pin the first N data columns after the label col on desktop (md+).
   * When set, mobile (xs–sm) scrolls the entire table with no sticky columns.
   */
  stickyLeadingDataColumns?: number;
  /** Fixed width for each sticky leading data column (defaults to `minDataColWidth`). */
  stickyLeadingDataColWidth?: number;
}> = ({
  columns,
  groups,
  minDataColWidth,
  stickyLeadingDataColumns = 0,
  stickyLeadingDataColWidth,
}) => {
  const colCount = columns.length + 1;
  const scrollColWidth = minDataColWidth ?? METRICS_TABLE_MIN_DATA_COL_WIDTH;
  const leadingColWidth = stickyLeadingDataColWidth ?? scrollColWidth;
  const leadingSticky = Math.min(stickyLeadingDataColumns, columns.length);
  const mobileScrollAll = leadingSticky > 0;

  const tableMinWidth =
    leadingSticky > 0
      ? getMetricsTableMinWidthWithLeadingSticky(
          columns.length,
          leadingSticky,
          leadingColWidth,
          scrollColWidth,
        )
      : getMetricsTableMinWidth(columns.length, scrollColWidth);

  const headerLabelSx = metricsTableStickyHeaderLabelCellSx(mobileScrollAll);
  const bodyLabelSx = metricsTableStickyLabelRowCellSx(mobileScrollAll);

  return (
    <Box sx={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
      <Table
        size="small"
        sx={{
          ...metricsTableBaseSx,
          minWidth: tableMinWidth,
          ...(mobileScrollAll
            ? {
                "& tbody th[scope='row']": bodyLabelSx,
              }
            : {}),
          "& tbody td": {
            fontVariantNumeric: "tabular-nums",
          },
        }}
      >
        <MetricsTableColgroup
          dataColumnCount={columns.length}
          fixedLeadingDataColumnCount={leadingSticky}
          fixedLeadingDataColWidth={leadingColWidth}
          scrollDataColWidth={scrollColWidth}
        />
        <TableHead>
          <TableRow>
            <TableCell sx={headerLabelSx} />
            {columns.map((col, colIdx) => (
              <TableCell
                key={col.id}
                align="left"
                sx={{
                  ...metricsTableDataHeaderCellSx,
                  ...metricsTableStickyDataCellSx(
                    colIdx,
                    leadingColWidth,
                    leadingSticky,
                    mobileScrollAll,
                    "header",
                  ),
                  overflow: "hidden",
                  maxWidth:
                    leadingSticky > 0 && colIdx < leadingSticky
                      ? leadingColWidth
                      : 0,
                  verticalAlign: col.subtitle ? "top" : "middle",
                }}
              >
                <MetricsTableColumnHeader col={col} />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => (
            <React.Fragment key={group.key}>
              {group.label ? (
                <TableRow>
                  <TableCell
                    colSpan={colCount}
                    sx={{
                      bgcolor: "action.hover",
                      py: 0.5,
                      position: mobileScrollAll ? { xs: "static", md: "sticky" } : "sticky",
                      left: mobileScrollAll ? { md: 0 } : 0,
                    }}
                  >
                    <Typography
                      variant="overline"
                      fontWeight={700}
                      color="text.secondary"
                    >
                      {group.label}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
              {group.rows.map((row) => (
                <TableRow key={row.key} hover>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={mobileScrollAll ? bodyLabelSx : undefined}
                  >
                    <Typography variant="body2" component="span" fontWeight={500} color="text.secondary">
                      {row.label}
                    </Typography>
                  </TableCell>
                  {row.values.slice(0, columns.length).map((value, idx) => (
                    <TableCell
                      key={columns[idx]!.id}
                      align="left"
                      sx={{
                        ...metricsTableDataBodyCellSx,
                        ...metricsTableStickyDataCellSx(
                          idx,
                          leadingColWidth,
                          leadingSticky,
                          mobileScrollAll,
                          "body",
                        ),
                      }}
                    >
                      {value}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
